import AdminAccounts from './AdminAccounts';
import AdminCityNews from './AdminCityNews';
import AdReportPanel from './AdReportPanel';
import AdBusinessPanel from './AdBusinessPanel';
import StaffRolesPanel from './StaffRolesPanel';
import { useEffect, useState } from 'react';
import { AlertCircle, Check, EyeOff, ShieldCheck, ToggleLeft, ToggleRight, RefreshCw } from 'lucide-react';
import { api, post } from './api';
import { text, type Locale } from '../shared/content';
import { errorText } from './i18n';
const copy = {
  accounts: text('Account reviews', '账号复核', '帳號複核', 'Revisiones'),
  news: text('City articles', '城市文章', '城市文章', 'Artículos'),
  title: text('City operations', '城市运营后台', '城市營運後台', 'Operaciones'),
  platform: text(
    'Platform moderation · separate from elected government',
    '平台管理权限 · 与玩家政府隔离',
    '平台管理權限 · 與玩家政府隔離',
    'Moderación de plataforma · separada del gobierno',
  ),
  refresh: text('Refresh', '刷新', '重新整理', 'Actualizar'),
  reports: text('Reports', '举报处理', '檢舉處理', 'Denuncias'),
  flags: text('Feature controls', '功能开关', '功能開關', 'Controles'),
  sponsors: text('Sponsorships', '赞助排期', '贊助排程', 'Patrocinios'),
  adReports: text('Ad reports', '广告报表', '廣告報表', 'Informes de anuncios'),
  staff: text('Operator roles', '岗位授权', '職務授權', 'Funciones'),
  alerts: text('Operations alerts', '运营告警', '營運警示', 'Alertas'),
  hide: text('Hide reported post', '隐藏被举报内容', '隱藏被檢舉內容', 'Ocultar publicación'),
  dismiss: text('Dismiss report', '结案不处理', '結案不處理', 'Archivar'),
  restrict: text('Restrict author · 7 days', '限制作者 7 天', '限制作者 7 天', 'Restringir autor · 7 días'),
  reason: text(
    'Restriction reason · minimum 5 characters',
    '限制理由 · 至少 5 字',
    '限制理由 · 至少 5 字',
    'Motivo · mínimo 5 caracteres',
  ),
  create: text('Create draft campaign', '创建赞助草稿', '建立贊助草稿', 'Crear borrador'),
  sponsor: text('Sponsor name', '赞助商名称', '贊助商名稱', 'Patrocinador'),
  placement: text('Placement', '展示位置', '展示位置', 'Ubicación'),
  headline: text('Headline', '标题', '標題', 'Título'),
  body: text('Message', '内容', '內容', 'Mensaje'),
  url: text('HTTPS destination', 'HTTPS 目标网址', 'HTTPS 目標網址', 'Destino HTTPS'),
  starts: text('Starts · local time', '开始时间 · 本地', '開始時間 · 本地', 'Inicio · hora local'),
  ends: text('Ends · local time', '结束时间 · 本地', '結束時間 · 本地', 'Fin · hora local'),
  approve: text('Approve', '审核通过', '審核通過', 'Aprobar'),
  pause: text('Pause', '暂停', '暫停', 'Pausar'),
  reject: text('Reject', '拒绝', '拒絕', 'Rechazar'),
  empty: text(
    'Nothing waiting here.',
    '目前没有待处理项目。',
    '目前沒有待處理項目。',
    'No hay nada pendiente.',
  ),
  policy: text(
    'Review sponsor identity and destination before approval. No real-world political campaigns, paid in-game influence or misleading claims.',
    '审核前核对赞助商身份与落地页。禁止现实政治宣传、付费游戏选举影响力或误导承诺。',
    '審核前核對贊助商身分與落地頁。禁止現實政治宣傳、付費遊戲選舉影響力或誤導承諾。',
    'Revisa patrocinador y destino. Sin campañas políticas reales, influencia electoral pagada ni promesas engañosas.',
  ),
  measurement: text(
    'Internal reports deduplicate each account per UTC day and add those daily totals. Registered QA accounts are excluded, including earlier claims; totals are not campaign-wide unique people or independently certified viewable impressions.',
    '内部统计按账号和 UTC 日去重后累加，排除已登记 QA（含早先上报）。累计值不是整个投放期的独立人数，也不是独立认证的可见曝光。',
    '內部統計按帳號和 UTC 日去重後累加，排除已登記 QA（含早先上報）。累計值不是整個投放期的獨立人數，也不是獨立認證的可見曝光。',
    'Los informes internos suman cuentas únicas por día UTC. Excluyen las cuentas QA registradas, incluso avisos anteriores. No son personas únicas de toda la campaña ni impresiones visibles certificadas.',
  ),
  measurementCounts: text(
    'Daily-deduplicated impression / click reports',
    '按日去重的曝光／点击上报',
    '按日去重的曝光／點擊上報',
    'Avisos diarios únicos de impresión / clic',
  ),
  residents: text('Residents', '居民', '居民', 'Residentes'),
  active: text('Active · 24h', '24h 活跃', '24h 活躍', 'Activos · 24h'),
  mail: text('Failed emails', '邮件失败', '郵件失敗', 'Correos fallidos'),
  events: text('Pending events', '待发事件', '待發事件', 'Eventos pendientes'),
};
type Capabilities = { admin: boolean; ads: boolean; moderation: boolean; any: boolean };
export function permittedOperatorTabs(capabilities: Capabilities) {
  return (
    ['reports', 'accounts', 'flags', 'sponsors', 'adReports', 'alerts', 'news', 'staff'] as const
  ).filter(
    (tab) =>
      capabilities.admin ||
      (['reports', 'accounts', 'news'].includes(tab)
        ? capabilities.moderation
        : ['sponsors', 'adReports'].includes(tab)
          ? capabilities.ads
          : false),
  );
}
export default function AdminPanel({
  locale,
  onMessage,
}: {
  locale: Locale;
  onMessage: (s: string, error?: boolean) => void;
}) {
  const t = (k: keyof typeof copy) => copy[k][locale];
  const [data, setData] = useState<any>(),
    [tab, setTab] = useState<
      'reports' | 'flags' | 'sponsors' | 'adReports' | 'alerts' | 'accounts' | 'news' | 'staff'
    >('reports'),
    [busy, setBusy] = useState(false),
    [reason, setReason] = useState('');
  const refresh = async () => {
    try {
      setData(await api(`/admin?lang=${locale}`));
    } catch (e) {
      setData(undefined);
      onMessage(errorText(locale, e), true);
    }
  };
  useEffect(() => {
    refresh();
  }, [locale]);
  const action = async (type: string, payload: Record<string, unknown>) => {
    setBusy(true);
    try {
      await post('/admin', { type, payload });
      if (type.startsWith('ad.') || (type === 'flag' && payload.name === 'ads'))
        window.dispatchEvent(new Event('ado-ads-changed'));
      await refresh();
    } catch (e) {
      onMessage(errorText(locale, e), true);
    } finally {
      setBusy(false);
    }
  };
  const allowedTabs = permittedOperatorTabs(
    data?.capabilities || { admin: false, ads: false, moderation: false, any: false },
  );
  const activeTab = allowedTabs.includes(tab) ? tab : allowedTabs[0];
  return (
    <>
      <div className="section-eyebrow">{t('platform')}</div>
      <h2>{t('title')}</h2>
      <button className="text-button" onClick={refresh}>
        <RefreshCw size={13} />
        {t('refresh')}
      </button>
      {data && (
        <>
          {data.capabilities?.admin && (
            <div className="admin-stats">
              {[
                ['residents', 'residents'],
                ['active_24h', 'active'],
                ['failed_mail', 'mail'],
                ['pending_events', 'events'],
              ].map(([key, label]) => (
                <div key={key}>
                  <b>{Number(data.stats[key!]).toLocaleString()}</b>
                  <small>{t(label as 'residents')}</small>
                </div>
              ))}
            </div>
          )}
          <div className="social-tabs admin-tabs" style={{ overflowX: 'auto' }}>
            {allowedTabs.map((k) => (
              <button
                className={activeTab === k ? 'active' : ''}
                key={k}
                onClick={() => setTab(k)}
                style={{ whiteSpace: 'nowrap' }}
              >
                {t(k)}
              </button>
            ))}
          </div>
          {activeTab === 'staff' && <StaffRolesPanel locale={locale} busy={busy} onAction={action} />}
          {activeTab === 'adReports' && <AdReportPanel locale={locale} />}
          {activeTab === 'news' && (
            <AdminCityNews articles={data.news || []} locale={locale} busy={busy} onAction={action} />
          )}
          {activeTab === 'accounts' && (
            <AdminAccounts
              locale={locale}
              appeals={data.appeals || []}
              busy={busy}
              onAction={action}
              onMessage={onMessage}
            />
          )}
          {activeTab === 'reports' &&
            (data.reports.length ? (
              data.reports.map((r: any) => (
                <article className="admin-report" key={r.id}>
                  <strong>{r.reason}</strong>
                  <small>
                    {r.reporter_name} · {new Date(r.created_at).toLocaleString()}
                  </small>
                  <blockquote>{r.evidence.original}</blockquote>
                  {r.evidence.translation?.body && (
                    <blockquote className="translated-evidence">{r.evidence.translation.body}</blockquote>
                  )}
                  <div className="friend-actions">
                    <button
                      className="button soft compact"
                      disabled={busy}
                      onClick={() => action('report', { reportId: r.id, action: 'hide-post' })}
                    >
                      <EyeOff size={12} />
                      {t('hide')}
                    </button>
                    <button
                      className="button light compact"
                      disabled={busy}
                      onClick={() => action('report', { reportId: r.id, action: 'dismiss' })}
                    >
                      {t('dismiss')}
                    </button>
                  </div>
                  {r.target_id && (
                    <>
                      <input
                        aria-label={t('reason')}
                        placeholder={t('reason')}
                        value={reason}
                        maxLength={500}
                        onChange={(e) => setReason(e.target.value)}
                      />
                      <button
                        className="text-button"
                        disabled={busy || reason.trim().length < 5}
                        onClick={() => action('restrict', { characterId: r.target_id, reason })}
                      >
                        {t('restrict')}
                      </button>
                    </>
                  )}
                </article>
              ))
            ) : (
              <p className="muted">{t('empty')}</p>
            ))}
          {activeTab === 'flags' && (
            <div className="feature-flags">
              {data.flags.map((f: any) => (
                <button
                  key={f.name}
                  disabled={busy}
                  onClick={() => action('flag', { name: f.name, enabled: !f.enabled })}
                >
                  <span>{f.name}</span>
                  {f.enabled ? <ToggleRight size={29} /> : <ToggleLeft size={29} />}
                </button>
              ))}
            </div>
          )}
          {activeTab === 'sponsors' && <AdBusinessPanel locale={locale} onMessage={onMessage} onChanged={refresh} />}
          {activeTab === 'alerts' &&
            (data.alerts.length ? (
              data.alerts.map((a: any) => (
                <article className="admin-report" key={a.id}>
                  <strong>
                    <AlertCircle size={14} />
                    {a.kind}
                  </strong>
                  <p>{JSON.stringify(a.details)}</p>
                  <button
                    className="text-button"
                    disabled={busy}
                    onClick={() => action('alert.resolve', { alertId: a.id })}
                  >
                    <Check size={13} />
                    {t('dismiss')}
                  </button>
                </article>
              ))
            ) : (
              <p className="muted">{t('empty')}</p>
            ))}
        </>
      )}
    </>
  );
}
